import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Loader2, CheckCircle2, Shield, Clock, Lock,
} from "lucide-react";

/*  Whodunit Week — 7-day email mystery landing + subscribe form.
    Honest zero-costs: enrollment stored in MongoDB, emails via existing
    `send_email` wrapper, no paid APIs. One-click unsubscribe baked in.
*/

const WhodunitWeek = () => {
  const [pitch, setPitch] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName]  = useState("");
  const [busy, setBusy]  = useState(false);
  const [done, setDone]  = useState(null);
  const [err, setErr]    = useState("");

  useEffect(() => {
    api.get("/whodunit-week/pitch")
      .then((r) => setPitch(r.data))
      .catch(() => setErr("Couldn't load the mystery details."));
  }, []);

  const subscribe = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true); setErr("");
    try {
      const country = (navigator.language || "").split("-").pop().toUpperCase().slice(0, 2);
      const { data } = await api.post("/whodunit-week/subscribe", {
        email, name, country,
        source: "landing",
        origin_url: window.location.origin,
      });
      setDone(data);
      if (data.already_enrolled) {
        toast("You're already enrolled, Detective.");
      } else {
        toast.success("You're in. Your first clue arrives tomorrow.");
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Enrollment failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050403] text-[#f4f0ec] pb-24"
      data-testid="whodunit-week-page">
      <div className="border-b border-[#2d2424] bg-[#0a0908]">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/"
            className="text-xs text-[#a39797] hover:text-[#f4f0ec] flex items-center gap-1.5"
            data-testid="ww-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
            NIGHTBLOOD CHRONICLES
          </div>
          <div className="w-14" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 pt-10">
        <div className="text-[10px] tracking-[0.3em] text-[#8b0000] mb-3">
          A SEVEN-DAY MYSTERY
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-[#f4f0ec] leading-tight">
          Whodunit Week
        </h1>
        <p className="font-hand text-[#d4af37] text-lg mt-3 rotate-[-0.5deg]">
          {pitch?.subtitle || "Seven days. One murder. One killer. You decide."}
        </p>

        {err && (
          <div className="text-sm text-[#f87171] mt-4" data-testid="ww-error">{err}</div>
        )}

        <div className="mt-8 card-noir p-6 bg-black/60 backdrop-blur-sm">
          <p className="text-[#c7bebe] leading-relaxed text-base">
            {pitch?.description || "A self-contained email mystery arrives in your inbox every morning for seven days. On Day 7, you submit your verdict and we tell you how close you got."}
          </p>
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="text-center">
              <Mail size={18} className="mx-auto text-[#d4af37] mb-1" />
              <div className="text-[10px] tracking-widest text-[#a39797]">EMAIL ONLY</div>
            </div>
            <div className="text-center">
              <Clock size={18} className="mx-auto text-[#d4af37] mb-1" />
              <div className="text-[10px] tracking-widest text-[#a39797]">7 MORNINGS</div>
            </div>
            <div className="text-center">
              <Shield size={18} className="mx-auto text-[#d4af37] mb-1" />
              <div className="text-[10px] tracking-widest text-[#a39797]">1-CLICK UNSUB</div>
            </div>
          </div>
        </div>

        {/* The 7-day arc preview */}
        {pitch?.day_titles && (
          <div className="mt-8" data-testid="ww-day-list">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">THE ARC</div>
            <ol className="space-y-2">
              {pitch.day_titles.map((t, i) => (
                <li key={i} className="flex items-center gap-3 card-noir px-4 py-2.5"
                    data-testid={`ww-day-row-${i + 1}`}>
                  <span className="font-mono text-xs text-[#8b0000] w-10">DAY {i + 1}</span>
                  <span className="text-sm text-[#f4f0ec] tracking-wider flex-1">{t}</span>
                  {i < pitch.day_titles.length - 1
                    ? <Lock size={12} className="text-[#5a5050]" />
                    : <span className="text-[10px] text-[#d4af37] tracking-widest">VERDICT</span>}
                </li>
              ))}
            </ol>
            {pitch?.blackout_dates?.length > 0 && (
              <div className="text-[10px] text-[#5a5050] mt-3 leading-relaxed">
                Blackout dates (no sends — the arc pauses so it never fights the Global Mystery Nights):
                {" "}
                {pitch.blackout_dates.map((d) => (
                  <span key={d} className="text-[#a39797] mr-2 font-mono">{d}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribe form OR thank-you panel */}
        {!done ? (
          <form onSubmit={subscribe}
            className="mt-10 card-noir p-6 bg-black/70 backdrop-blur-sm border-[#d4af37]">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-4">ENROLL — FREE</div>
            <input type="email" required placeholder="Your email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-3 focus:outline-none focus:border-[#d4af37]"
              data-testid="ww-email" />
            <input placeholder="Your name (optional)"
              value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-4 focus:outline-none focus:border-[#d4af37]"
              data-testid="ww-name" />
            <button type="submit" disabled={busy}
              className="btn-primary w-full justify-center disabled:opacity-50"
              data-testid="ww-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Enroll — my first clue arrives tomorrow
            </button>
            <div className="text-[10px] text-[#5a5050] text-center mt-3">
              No spam. No partners. One-click unsubscribe in every email.
            </div>
          </form>
        ) : (
          <div className="mt-10 card-noir p-6 bg-black/70 backdrop-blur-sm text-center"
            data-testid="ww-thanks">
            <CheckCircle2 size={44} className="text-[#4ade80] mx-auto mb-4" />
            <div className="font-display text-3xl text-[#f4f0ec] mb-1">
              {done.already_enrolled ? "Already on the case." : "You're in, Detective."}
            </div>
            <div className="text-[#a39797] text-sm mb-5">
              {done.already_enrolled
                ? "You're already enrolled. Your clues will keep arriving on schedule."
                : "Your first clue arrives at 15:00 UTC. Keep an eye on your inbox."}
            </div>
            <Link to="/" className="btn-primary"
              data-testid="ww-home">
              Enter the site →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhodunitWeek;
